import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import axios from 'axios';
import OptimizedImage from './OptimizedImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeaturedProducts = ({ limit, showTitle = true, className = '' }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ featured_shop_enabled: true, featured_shop_count: 4 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch settings and products in parallel
        const [settingsRes, productsRes] = await Promise.all([
          axios.get(`${API}/site-settings/`).catch(() => ({ data: {} })),
          axios.get(`${API}/products/`, { params: { featured: true } }),
        ]);
        
        const s = settingsRes.data || {};
        setSettings(s);
        
        if (s.featured_shop_enabled === false) {
          setProducts([]);
          return;
        }
        
        const count = limit || s.featured_shop_count || 4;
        // Deduplicate by product name
        const seen = new Set();
        const unique = (productsRes.data || []).filter(p => {
          const key = p.name?.replace(/\s*\(Kopie\)\s*/g, '').trim();
          if (seen.has(key)) return false;
          seen.add(key);
          return p.in_stock !== false;
        });
        setProducts(unique.slice(0, count));
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [limit]);

  if (loading || products.length === 0) return null;

  return (
    <section className={`${className}`} data-testid="featured-products">
      {showTitle && (
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-terra-beige" />
          <h2 className="font-sweet-sans text-lg text-earth-brown uppercase tracking-widest">
            Uitgelicht
          </h2>
          <div className="flex-1 h-px bg-terra-beige/30 ml-3" />
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((product) => (
          <Link
            key={product.id}
            to={`/product/${product.id}`}
            className="group block"
            data-testid={`featured-product-${product.id}`}
          >
            <div className="relative aspect-square overflow-hidden bg-gray-50 mb-2">
              <OptimizedImage
                src={product.image}
                cloudinaryUrl={product.cloudinary_image_url}
                alt={product.name}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-2 left-2">
                <span className="bg-terra-beige text-white px-2 py-0.5 text-[10px] font-sweet font-semibold uppercase tracking-wider">
                  Uitgelicht
                </span>
              </div>
            </div>
            <h3 className="font-sweet-sans text-xs sm:text-sm text-earth-brown uppercase tracking-wide group-hover:text-terra-beige transition-colors line-clamp-1">
              {product.name}
            </h3>
            <p className="font-sweet-sans text-sm font-semibold text-terra-beige mt-0.5">
              €{(product.price || 0).toFixed(2)}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
};
